import {useCallback, useEffect} from 'react'
import {useLocalStorage} from '@/shared/lib'
import {STORAGE_KEYS} from '@/shared/config'
import {Alert, AlertDescription, AlertTitle} from '@/shared/ui'
import {useMediaDevices} from '../device-selector'
import {useAudioSettings, usePreviewAudioLevel} from '../audio-settings'
import {useMediaPreview} from './useMediaPreview'
import {VideoPreviewSection} from './VideoPreviewSection'
import {LobbyForm} from './LobbyForm'

interface PreJoinLobbyProps {
  onJoin: (username: string, audioEnabled: boolean, videoEnabled: boolean) => void
}

export function PreJoinLobby({onJoin}: PreJoinLobbyProps) {
  const [username, setUsername] = useLocalStorage<string>(STORAGE_KEYS.USERNAME, '')

  const {
    isLoading,
    isVideoLoading,
    error,
    hasAudio,
    hasVideo,
    isAudioEnabled,
    isVideoEnabled,
    isFrontCamera,
    toggleAudio,
    toggleVideo,
    videoRefCallback,
    stopAllTracks,
    deviceWarning,
    audioMediaStreamTrack,
    switchAudioDevice,
    switchVideoDevice,
  } = useMediaPreview()

  const audioDevices = useMediaDevices(
    'audioinput',
    useCallback((deviceId: string) => void switchAudioDevice(deviceId), [switchAudioDevice]),
  )
  const videoDevices = useMediaDevices(
    'videoinput',
    useCallback((deviceId: string) => void switchVideoDevice(deviceId), [switchVideoDevice]),
  )

  // After media tracks are initialized, re-enumerate to get device labels.
  // Browsers only expose labels once a media stream is active in the page.
  useEffect(() => {
    if (!isLoading) {
      void audioDevices.refreshDevices()
      void videoDevices.refreshDevices()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const handleSelectAudioDevice = useCallback(
    (deviceId: string) => {
      if (deviceId === audioDevices.selectedDeviceId) return
      audioDevices.selectDevice(deviceId)
      void switchAudioDevice(deviceId)
    },
    [audioDevices, switchAudioDevice],
  )

  const handleSelectVideoDevice = useCallback(
    (deviceId: string) => {
      if (deviceId === videoDevices.selectedDeviceId) return
      videoDevices.selectDevice(deviceId)
      void switchVideoDevice(deviceId)
    },
    [videoDevices, switchVideoDevice],
  )

  // Audio settings (noise gate threshold)
  const {threshold, setThreshold, mode, setMode} = useAudioSettings()
  const {level: previewAudioLevel, autoThreshold: previewAutoThreshold} =
    usePreviewAudioLevel(audioMediaStreamTrack)

  const handleJoin = useCallback(() => {
    const finalUsername = String(username).trim() || 'Гость'
    setUsername(finalUsername)

    // Stop preview tracks - LiveKit will create new ones when joining
    stopAllTracks()

    onJoin(finalUsername, isAudioEnabled && hasAudio, isVideoEnabled && hasVideo)
  }, [
    username,
    setUsername,
    onJoin,
    isAudioEnabled,
    isVideoEnabled,
    hasAudio,
    hasVideo,
    stopAllTracks,
  ])

  const isReady = !isLoading

  if (error) {
    return (
      <div className="flex flex-1 flex-col px-8 py-12 max-sm:px-4 max-sm:py-8 [@media(max-height:700px)]:py-6">
        <div className="my-auto w-full max-w-[480px] mx-auto animate-in fade-in slide-in-from-bottom-5 duration-400 rounded-3xl bg-card/95 p-8 shadow-2xl backdrop-blur-xl max-sm:p-6">
          <Alert variant="error">
            <AlertTitle>Нет доступа к камере и микрофону</AlertTitle>
            <AlertDescription>
              <p>{error}</p>
              <p>Разрешите доступ к камере и микрофону в настройках браузера.</p>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col px-8 py-12 max-sm:px-4 max-sm:py-8 [@media(max-height:700px)]:py-3">
      <div className="my-auto w-full max-w-[480px] mx-auto animate-in fade-in slide-in-from-bottom-5 duration-400 rounded-3xl bg-card/95 p-8 shadow-2xl backdrop-blur-xl max-sm:p-6">
        <h2 className="mb-6 text-center text-2xl font-semibold text-foreground max-sm:text-xl">
          Готовы присоединиться?
        </h2>

        <VideoPreviewSection
          isLoading={isLoading}
          isVideoLoading={isVideoLoading}
          isVideoEnabled={isVideoEnabled}
          isAudioEnabled={isAudioEnabled}
          hasVideo={hasVideo}
          hasAudio={hasAudio}
          isFrontCamera={isFrontCamera}
          videoRefCallback={videoRefCallback}
          onToggleAudio={toggleAudio}
          onToggleVideo={toggleVideo}
          audioDevices={audioDevices.devices}
          selectedAudioDeviceId={audioDevices.selectedDeviceId}
          onSelectAudioDevice={handleSelectAudioDevice}
          videoDevices={videoDevices.devices}
          selectedVideoDeviceId={videoDevices.selectedDeviceId}
          onSelectVideoDevice={handleSelectVideoDevice}
          audioLevel={isAudioEnabled ? previewAudioLevel : 0}
          audioAutoThreshold={previewAutoThreshold}
          audioThreshold={threshold}
          audioMode={mode}
          onAudioThresholdChange={setThreshold}
          onAudioModeChange={setMode}
        />

        <LobbyForm
          username={username}
          onUsernameChange={setUsername}
          onJoin={handleJoin}
          deviceWarning={deviceWarning}
          isReady={isReady}
        />
      </div>
    </div>
  )
}
